import { useState, useEffect } from 'react';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';

/**
 * Hook to manage widget configuration state and operations
 */
export const useWidgetConfig = (configId, formData = {}) => {
  const [widgetConfig, setWidgetConfig] = useState({
    welcome_message: '',
    widget_prompt: '',
    widget_title: '',
    widget_subtitle: '',
    widget_questions: [],
    widget_type: 'Web Widget',
    widget_color: '#10b981',
    widget_position: 'right',
    widget_language: 'pl-pl',
    widget_link: '',
    auto_detect_domains: true,
    extract_page_context: true,
    widget_assistant_name: null,
    cdn_images_url: '',
    status: 'published',
    ...formData,
    // Override widget_questions if provided in formData
    ...(formData.widget_questions && { widget_questions: Array.isArray(formData.widget_questions) ? formData.widget_questions : [] })
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        if (!configId) {
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('directus_token');
        if (!token) {
          setError('Authentication required to load widget configuration');
          setLoading(false);
          return;
        }

        try {
          const configResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/widget_config/${configId}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              fields: ['*', 'user_created.first_name', 'user_created.last_name', 'user_created.email']
            }
          });
          
          if (configResponse.data.data) {
            const config = configResponse.data.data;
            setWidgetConfig({
              id: config.id,
              welcome_message: config.welcome_message || '',
              widget_prompt: config.widget_prompt || '',
              widget_title: config.widget_title || '',
              widget_subtitle: config.widget_subtitle || '',
              widget_questions: Array.isArray(config.widget_questions) ? config.widget_questions : [],
              status: config.status || 'published',
              widget_type: config.widget_type || 'Web Widget',
              widget_color: config.widget_color || '#10b981',
              widget_position: config.widget_position || 'right',
              widget_language: config.widget_language || 'pl-pl',
              widget_link: config.widget_link || '',
              auto_detect_domains: typeof config.auto_detect_domains === 'boolean' ? config.auto_detect_domains : true,
              extract_page_context: typeof config.extract_page_context === 'boolean' ? config.extract_page_context : true,
              widget_assistant_name: config.widget_assistant_name || null,
              cdn_images_url: config.cdn_images_url || '',
              widget_linked_workspace: config.widget_linked_workspace || null,
              date_created: config.date_created,
              date_updated: config.date_updated,
              user_created: config.user_created
            });
          }
        } catch (configError) {
          setError('Error loading widget configuration');
        }
      } catch (error) {
        setError('Error loading configuration data');
      } finally {
        setLoading(false);
      }
    };

    fetchWidgetConfig();
  }, [configId]);

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setWidgetConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleStatus = () => {
    setWidgetConfig(prev => ({
      ...prev,
      status: prev.status === 'published' ? 'draft' : 'published'
    }));
  };

  const saveWidgetConfig = async (workspaceId, onSuccess) => {
    setSaving(true);
    
    try {
      const token = localStorage.getItem('directus_token');
      if (!token) {
        setError('Authentication token not found');
        setSaving(false);
        return;
      }

      const configData = {
        welcome_message: widgetConfig.welcome_message,
        widget_prompt: widgetConfig.widget_prompt || '',
        widget_title: widgetConfig.widget_title || '',
        widget_subtitle: widgetConfig.widget_subtitle || '',
        widget_linked_workspace: workspaceId,
        widget_questions: Array.isArray(widgetConfig.widget_questions) ? widgetConfig.widget_questions : [],
        status: widgetConfig.status || 'published',
        widget_type: widgetConfig.widget_type || 'Web Widget',
        widget_color: widgetConfig.widget_color || '#10b981',
        widget_position: widgetConfig.widget_position || 'right',
        widget_language: widgetConfig.widget_language || 'pl-pl',
        widget_link: widgetConfig.widget_link || '',
        auto_detect_domains: typeof widgetConfig.auto_detect_domains === 'boolean' ? widgetConfig.auto_detect_domains : true,
        extract_page_context: typeof widgetConfig.extract_page_context === 'boolean' ? widgetConfig.extract_page_context : true,
        widget_assistant_name: widgetConfig.widget_assistant_name?.trim() || null,
        cdn_images_url: widgetConfig.cdn_images_url || ''
      };
      
      // If we have an existing ID, update; otherwise create
      if (widgetConfig.id) {
        await axios.patch(
          `${DIRECTUS_INSTANCE}/items/widget_config/${widgetConfig.id}`,
          configData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const response = await axios.post(
          `${DIRECTUS_INSTANCE}/items/widget_config`,
          configData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Set the ID from the response
        setWidgetConfig(prev => ({
          ...prev,
          id: response.data.data.id
        }));
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setError('Error saving widget configuration');
    } finally {
      setSaving(false);
    }
  };

  return {
    widgetConfig,
    setWidgetConfig,
    loading,
    saving,
    error,
    handleConfigChange,
    toggleStatus,
    saveWidgetConfig
  };
};

